import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { costingAPI } from '../../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { useConfirm } from '@/components/ConfirmProvider';
import { toast } from 'sonner';
import { Loader2, Settings2, Plus, Trash2, FileSpreadsheet, FileText, TrendingUp, TrendingDown, ArrowUp, ArrowDown, ArrowUpDown, Camera, GitCompare, X } from 'lucide-react';
import { GREEN, RUST, MUTED, fmtRate, fmt2, downloadBlob, fmtDateTime } from './helpers';


const BASIS_LABEL = { per_batch: 'Per Batch', per_kg: 'Per kg', per_bag: 'Per Bag' };


// Scope picker: an overhead applies to All products, or a chosen subset.
const OverheadScope = ({ scope, productIds, products, onChange }) => {
  const specific = scope === 'specific';
  const selected = productIds || [];
  const toggle = (pid) => {
    const next = selected.includes(pid) ? selected.filter((x) => x !== pid) : [...selected, pid];
    onChange({ applies_to: 'specific', product_ids: next });
  };
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="overhead-scope">
      <Select value={specific ? 'specific' : 'all'} onValueChange={(v) => onChange(v === 'all' ? { applies_to: 'all', product_ids: [] } : { applies_to: 'specific', product_ids: selected })}>
        <SelectTrigger className="h-8 w-36" data-testid="overhead-scope-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All products</SelectItem>
          <SelectItem value="specific">Specific products</SelectItem>
        </SelectContent>
      </Select>
      {specific && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8" data-testid="overhead-scope-products">
              {selected.length ? `${selected.length} product${selected.length > 1 ? 's' : ''}` : 'Pick products'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2 max-h-72 overflow-y-auto" align="start">
            {products.map((p) => (
              <label key={p.product_id} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:bg-gray-50 rounded px-1">
                <Checkbox checked={selected.includes(p.product_id)} onCheckedChange={() => toggle(p.product_id)} data-testid={`overhead-scope-opt-${p.product_id}`} />
                <span>{p.product_name}</span>
              </label>
            ))}
            {products.length === 0 && <p className="text-xs text-center py-2" style={{ color: MUTED }}>No products with recipes yet.</p>}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};


export const CostSheetSection = ({ refreshKey }) => {
  const confirm = useConfirm();
  const [formulations, setFormulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productId, setProductId] = useState('');
  const [batchSize, setBatchSize] = useState('');
  const [bagWeight, setBagWeight] = useState('50');
  const [sheet, setSheet] = useState(null);
  const [computing, setComputing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [summaries, setSummaries] = useState([]);
  const [sSortKey, setSSortKey] = useState('product_name');
  const [sSortDir, setSSortDir] = useState('asc');
  const [snapshots, setSnapshots] = useState([]);
  const [snapDialog, setSnapDialog] = useState(false);
  const [snapLabel, setSnapLabel] = useState('');
  const [snapBusy, setSnapBusy] = useState(false);
  const [compareId, setCompareId] = useState('');
  const [summaryExporting, setSummaryExporting] = useState(false);


  const loadSnapshots = useCallback(async () => {
    try {
      const res = await costingAPI.listSnapshots();
      setSnapshots(res.data || []);
    } catch (e) { /* non-blocking */ }
  }, []);


  const loadSummaries = useCallback(async (bw) => {
    try {
      const res = await costingAPI.listCostSheets({ bag_weight_kg: parseFloat(bw) || 50 });
      setSummaries(res.data || []);
    } catch (e) { /* non-blocking */ }
  }, []);


  // overheads
  const [ohDialog, setOhDialog] = useState(false);
  const [overheads, setOverheads] = useState([]);
  const [ohForm, setOhForm] = useState({ name: '', basis: 'per_batch', amount: '', applies_to: 'all', product_ids: [] });
  const [ohBusy, setOhBusy] = useState(false);


  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await costingAPI.listFormulations();
      setFormulations(res.data || []);
      await loadSummaries(bagWeight);
      await loadSnapshots();
    } catch (e) {
      toast.error('Failed to load formulations');
    } finally { setLoading(false); }
  }, [loadSummaries, loadSnapshots, bagWeight]);


  useEffect(() => { loadForms(); }, [loadForms, refreshKey]);


  const compute = useCallback(async (pid, bsz, bw) => {
    if (!pid) { setSheet(null); return; }
    setComputing(true);
    try {
      const params = { bag_weight_kg: parseFloat(bw) || 50 };
      const b = parseFloat(bsz);
      if (b && b > 0) params.batch_size_kg = b;
      const res = await costingAPI.getCostSheet(pid, params);
      setSheet(res.data);
    } catch (e) {
      setSheet(null);
      toast.error(e?.response?.data?.detail || 'No formulation for this product');
    } finally { setComputing(false); }
  }, []);


  const onPickProduct = (pid) => {
    setProductId(pid);
    const f = formulations.find((x) => x.product_id === pid);
    const bsz = f ? String(f.batch_size_kg) : '';
    setBatchSize(bsz);
    compute(pid, bsz, bagWeight);
  };


  const reCompute = () => { compute(productId, batchSize, bagWeight); loadSummaries(bagWeight); };


  const loadOverheads = async () => {
    try {
      const res = await costingAPI.listOverheads();
      setOverheads(res.data || []);
    } catch (e) { toast.error('Failed to load overheads'); }
  };
  const openOverheads = async () => { await loadOverheads(); setOhDialog(true); };
  const addOverhead = async () => {
    const name = ohForm.name.trim();
    const amount = parseFloat(ohForm.amount);
    if (!name) { toast.error('Name required'); return; }
    if (isNaN(amount) || amount < 0) { toast.error('Valid amount required'); return; }
    setOhBusy(true);
    try {
      await costingAPI.createOverhead({ name, basis: ohForm.basis, amount, applies_to: ohForm.applies_to, product_ids: ohForm.applies_to === 'specific' ? ohForm.product_ids : [] });
      setOhForm({ name: '', basis: 'per_batch', amount: '', applies_to: 'all', product_ids: [] });
      await loadOverheads();
      toast.success('Overhead added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
    finally { setOhBusy(false); }
  };
  const updateOverhead = async (oh, patch) => {
    try {
      await costingAPI.updateOverhead(oh.id, patch);
      await loadOverheads();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };
  const deleteOverhead = async (oh) => {
    const ok = await confirm({ title: 'Delete overhead?', description: `"${oh.name}" will be removed.`, confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await costingAPI.deleteOverhead(oh.id);
      setOverheads((p) => p.filter((x) => x.id !== oh.id));
      toast.success('Overhead deleted');
    } catch (e) { toast.error('Delete failed'); }
  };


  const exportSheet = async (kind) => {
    if (!productId) return;
    setExporting(true);
    try {
      const params = { bag_weight_kg: parseFloat(bagWeight) || 50 };
      const b = parseFloat(batchSize);
      if (b && b > 0) params.batch_size_kg = b;
      const res = kind === 'excel'
        ? await costingAPI.exportCostSheetExcel(productId, params)
        : await costingAPI.exportCostSheetPdf(productId, params);
      downloadBlob(res, `cost_sheet.${kind === 'excel' ? 'xlsx' : 'pdf'}`);
    } catch (e) { toast.error('Export failed'); }
    finally { setExporting(false); }
  };


  const exportSummary = async (kind) => {
    setSummaryExporting(true);
    try {
      const params = { bag_weight_kg: parseFloat(bagWeight) || 50 };
      const res = kind === 'excel' ? await costingAPI.exportCostSummaryExcel(params) : await costingAPI.exportCostSummaryPdf(params);
      downloadBlob(res, `cost_summary.${kind === 'excel' ? 'xlsx' : 'pdf'}`);
    } catch (e) { toast.error('Export failed'); }
    finally { setSummaryExporting(false); }
  };


  const saveSnapshot = async () => {
    setSnapBusy(true);
    try {
      await costingAPI.createSnapshot({ label: snapLabel.trim() || null, bag_weight_kg: parseFloat(bagWeight) || 50 });
      setSnapLabel('');
      await loadSnapshots();
      toast.success('Snapshot saved');
    } catch (e) { toast.error('Save failed'); }
    finally { setSnapBusy(false); }
  };
  const removeSnapshot = async (s) => {
    const ok = await confirm({ title: 'Delete snapshot?', description: `${s.label || 'Snapshot'} (${fmtDateTime(s.created_at)}) will be removed.`, confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await costingAPI.deleteSnapshot(s.id);
      setSnapshots((p) => p.filter((x) => x.id !== s.id));
      if (compareId === s.id) setCompareId('');
      toast.success('Snapshot deleted');
    } catch (e) { toast.error('Delete failed'); }
  };


  const compareSnap = useMemo(() => snapshots.find((s) => s.id === compareId) || null, [snapshots, compareId]);
  const comparison = useMemo(() => {
    if (!compareSnap) return [];
    const snapMap = Object.fromEntries((compareSnap.items || []).map((i) => [i.product_id, i]));
    return summaries.map((cur) => {
      const prev = snapMap[cur.product_id];
      return {
        product_id: cur.product_id,
        product_name: cur.product_name,
        then_kg: prev ? prev.cost_per_kg : null,
        now_kg: cur.cost_per_kg,
        then_bag: prev ? prev.cost_per_bag : null,
        now_bag: cur.cost_per_bag,
        then_mpct: prev ? prev.margin_pct : null,
        now_mpct: cur.margin_pct,
      };
    });
  }, [compareSnap, summaries]);


  const marginPositive = sheet && sheet.margin_abs >= 0;


  const sortedSummaries = useMemo(() => {
    const dir = sSortDir === 'asc' ? 1 : -1;
    return summaries.slice().sort((a, b) => {
      if (sSortKey === 'product_name') return `${a.product_name}`.localeCompare(`${b.product_name}`) * dir;
      return ((a[sSortKey] ?? 0) - (b[sSortKey] ?? 0)) * dir;
    });
  }, [summaries, sSortKey, sSortDir]);
  const toggleSSort = (k) => {
    if (sSortKey === k) setSSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSSortKey(k); setSSortDir('asc'); }
  };
  const SSortHead = ({ label, k, align = 'right' }) => (
    <th className={`px-3 py-2 font-semibold cursor-pointer select-none ${align === 'right' ? 'text-right' : 'text-left'}`} onClick={() => toggleSSort(k)} data-testid={`costsheet-sort-${k}`}>
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {sSortKey === k ? (sSortDir === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />) : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
      </span>
    </th>
  );


  const SummaryTable = () => (
    <Card className="overflow-hidden" data-testid="costsheet-summary-table">
      <div className="px-4 py-2.5 flex flex-wrap items-center gap-2" style={{ background: '#EFF4EE' }}>
        <span className="text-sm font-semibold" style={{ color: GREEN }}>All Products — Cost Summary ({summaries.length})</span>
        <div className="flex-1" />
        <div className="w-52">
          <Select value={compareId || 'none'} onValueChange={(v) => setCompareId(v === 'none' ? '' : v)}>
            <SelectTrigger className="h-8" data-testid="costsheet-compare-select"><SelectValue placeholder="Compare vs snapshot…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No comparison</SelectItem>
              {snapshots.map((s) => (<SelectItem key={s.id} value={s.id}>{s.label || 'Snapshot'} · {new Date(s.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="outline" className="h-8" onClick={() => setSnapDialog(true)} data-testid="costsheet-snapshots-btn"><Camera className="w-4 h-4 mr-1.5" /> Snapshots</Button>
        <Button size="sm" variant="outline" className="h-8" onClick={() => exportSummary('excel')} disabled={summaryExporting || summaries.length === 0} data-testid="costsheet-summary-excel"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel</Button>
        <Button size="sm" variant="outline" className="h-8" onClick={() => exportSummary('pdf')} disabled={summaryExporting || summaries.length === 0} data-testid="costsheet-summary-pdf"><FileText className="w-4 h-4 mr-1.5" /> PDF</Button>
      </div>
      {summaries.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm" style={{ color: MUTED }}>No formulations yet — build one to see its cost summary.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: '#FBF9F6', color: MUTED }} className="text-xs uppercase tracking-wide">
                <SSortHead label="Product" k="product_name" align="left" />
                <SSortHead label="Per-batch Total" k="per_batch_total" />
                <SSortHead label="Cost / kg" k="cost_per_kg" />
                <SSortHead label="Cost / bag" k="cost_per_bag" />
                <SSortHead label="Net Sell / bag" k="net_selling_price" />
                <SSortHead label="Margin ₹" k="margin_abs" />
                <SSortHead label="Margin %" k="margin_pct" />
              </tr>
            </thead>
            <tbody>
              {sortedSummaries.map((s) => {
                const pos = (s.margin_abs ?? 0) >= 0;
                return (
                  <tr key={s.product_id} className="border-t hover:bg-[#FBF9F6] cursor-pointer" style={{ borderColor: '#F0EEE9' }} onClick={() => onPickProduct(s.product_id)} data-testid="costsheet-summary-row">
                    <td className="px-3 py-2 font-medium" style={{ color: '#1E2420' }}>{s.product_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: GREEN }}>{fmtRate(s.per_batch_total)}<div className="text-[10px] font-normal" style={{ color: MUTED }}>{fmt2(s.batch_size_kg)} kg batch</div></td>
                    <td className="px-3 py-2 text-right tabular-nums" style={{ color: GREEN }}>{fmtRate(s.cost_per_kg)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: GREEN }}>{fmtRate(s.cost_per_bag)}<div className="text-[10px] font-normal" style={{ color: MUTED }}>{fmt2(s.bag_weight_kg)} kg · {fmt2(s.bags_per_batch)} bags</div></td>
                    <td className="px-3 py-2 text-right tabular-nums" style={{ color: MUTED }}>{fmtRate(s.net_selling_price)}<div className="text-[10px] font-normal" style={{ color: MUTED }}>list {fmtRate(s.selling_price)} −11%</div></td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: pos ? GREEN : RUST }}>{fmtRate(s.margin_abs)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: pos ? GREEN : RUST }}>{s.margin_pct == null ? 'N/A' : `${fmt2(s.margin_pct)}%`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );


  return (
    <div>
      {loading ? (
        <div className="flex items-center gap-2 py-16 justify-center" style={{ color: MUTED }}><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : (
        <>
          <Card className="p-4 mb-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-52">
                <label className="text-xs font-medium block mb-1" style={{ color: MUTED }}>Product (with recipe)</label>
                <Select value={productId} onValueChange={onPickProduct}>
                  <SelectTrigger data-testid="costsheet-product-select"><SelectValue placeholder="Select a product" /></SelectTrigger>
                  <SelectContent>
                    {formulations.length === 0 && <div className="px-3 py-2 text-sm" style={{ color: MUTED }}>No formulations yet — build one first.</div>}
                    {formulations.map((f) => (<SelectItem key={f.product_id} value={f.product_id}>{f.product_name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <label className="text-xs font-medium block mb-1" style={{ color: MUTED }}>Batch (kg)</label>
                <Input data-testid="costsheet-batch" type="number" min="1" value={batchSize} onChange={(e) => setBatchSize(e.target.value)} onBlur={reCompute} />
              </div>
              <div className="w-32">
                <label className="text-xs font-medium block mb-1" style={{ color: MUTED }}>Bag (kg)</label>
                <Input data-testid="costsheet-bag" type="number" min="1" value={bagWeight} onChange={(e) => setBagWeight(e.target.value)} onBlur={reCompute} />
              </div>
              <Button variant="outline" className="h-9" onClick={openOverheads} data-testid="costsheet-manage-overheads"><Settings2 className="w-4 h-4 mr-1.5" /> Overheads</Button>
              <div className="flex-1" />
              <Button variant="outline" className="h-9" onClick={() => exportSheet('excel')} disabled={!sheet || exporting} data-testid="costsheet-export-excel"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel</Button>
              <Button variant="outline" className="h-9" onClick={() => exportSheet('pdf')} disabled={!sheet || exporting} data-testid="costsheet-export-pdf"><FileText className="w-4 h-4 mr-1.5" /> PDF</Button>
            </div>
          </Card>


          {computing ? (
            <div className="flex items-center gap-2 py-16 justify-center" style={{ color: MUTED }}><Loader2 className="w-5 h-5 animate-spin" /> Computing…</div>
          ) : !sheet ? (
            <Card className="p-10 text-center" style={{ color: MUTED }} data-testid="costsheet-empty">Pick a product with a saved formulation to see its cost sheet.</Card>
          ) : (
            <div className="space-y-4" data-testid="costsheet-result">
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-4"><p className="text-xs uppercase" style={{ color: MUTED }}>Per-batch Total</p><p className="text-xl font-bold" style={{ color: GREEN }}>{fmtRate(sheet.per_batch_total)}</p><p className="text-[11px]" style={{ color: MUTED }}>{fmt2(sheet.batch_size_kg)} kg batch</p></Card>
                <Card className="p-4"><p className="text-xs uppercase" style={{ color: MUTED }}>Cost / kg</p><p className="text-xl font-bold" style={{ color: GREEN }}>{fmtRate(sheet.cost_per_kg)}</p></Card>
                <Card className="p-4"><p className="text-xs uppercase" style={{ color: MUTED }}>Cost / Bag</p><p className="text-xl font-bold" style={{ color: GREEN }}>{fmtRate(sheet.cost_per_bag)}</p><p className="text-[11px]" style={{ color: MUTED }}>{fmt2(sheet.bag_weight_kg)} kg bag · {fmt2(sheet.bags_per_batch)} bags/batch</p></Card>
                <Card className="p-4" data-testid="costsheet-margin">
                  <p className="text-xs uppercase flex items-center gap-1" style={{ color: MUTED }}>Margin vs Net Sell {marginPositive ? <TrendingUp className="w-3.5 h-3.5" style={{ color: GREEN }} /> : <TrendingDown className="w-3.5 h-3.5" style={{ color: RUST }} />}</p>
                  <p className="text-xl font-bold" style={{ color: marginPositive ? GREEN : RUST }}>{fmtRate(sheet.margin_abs)}</p>
                  <p className="text-[11px]" style={{ color: MUTED }}>Net Sell {fmtRate(sheet.net_selling_price)} / bag (list {fmtRate(sheet.selling_price)} −11%) · {sheet.margin_pct == null ? 'N/A' : `${fmt2(sheet.margin_pct)}%`}</p>
                </Card>
              </div>


              {/* RM breakdown */}
              <Card className="overflow-hidden">
                <div className="px-4 py-2.5 text-sm font-semibold" style={{ background: '#EFF4EE', color: GREEN }}>Raw Materials</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: '#FBF9F6', color: MUTED }} className="text-left text-xs uppercase">
                      <th className="px-4 py-2">Material</th><th className="px-4 py-2">Category</th>
                      <th className="px-4 py-2 text-right">%</th><th className="px-4 py-2 text-right">Qty (kg)</th>
                      <th className="px-4 py-2 text-right">Rate</th><th className="px-4 py-2 text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.rm_rows.map((r, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: '#F0EEE9' }} data-testid="costsheet-rm-row">
                        <td className="px-4 py-2 font-medium" style={{ color: r.missing ? RUST : '#1E2420' }}>{r.rm_name}</td>
                        <td className="px-4 py-2" style={{ color: MUTED }}>{r.category}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmt2(r.percent)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmt2(r.kg_at_batch)}</td>
                        <td className="px-4 py-2 text-right tabular-nums" style={{ color: MUTED }}>{fmtRate(r.rate_per_kg)}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-semibold" style={{ color: GREEN }}>{fmtRate(r.cost_at_batch)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2" style={{ borderColor: GREEN, background: '#FBF9F6' }}>
                      <td className="px-4 py-2 font-bold" style={{ color: GREEN }} colSpan={2}>RM Cost · {fmt2(sheet.percent_total)}%</td>
                      <td colSpan={3} />
                      <td className="px-4 py-2 text-right font-bold" style={{ color: GREEN }}>{fmtRate(sheet.rm_cost)}</td>
                    </tr>
                  </tfoot>
                </table>
              </Card>


              {/* Overheads */}
              <Card className="overflow-hidden">
                <div className="px-4 py-2.5 text-sm font-semibold" style={{ background: '#F6E7DF', color: RUST }}>Overheads</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: '#FBF9F6', color: MUTED }} className="text-left text-xs uppercase">
                      <th className="px-4 py-2">Head</th><th className="px-4 py-2">Basis</th>
                      <th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2 text-right">Contribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.overhead_rows.map((r, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: '#F0EEE9' }} data-testid="costsheet-oh-row">
                        <td className="px-4 py-2 font-medium">{r.name}</td>
                        <td className="px-4 py-2" style={{ color: MUTED }}>{BASIS_LABEL[r.basis] || r.basis}</td>
                        <td className="px-4 py-2 text-right tabular-nums" style={{ color: MUTED }}>{fmtRate(r.amount)}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-semibold" style={{ color: RUST }}>{fmtRate(r.contribution)}</td>
                      </tr>
                    ))}
                    {sheet.overhead_rows.length === 0 && <tr><td colSpan={4} className="px-4 py-4 text-center text-sm" style={{ color: MUTED }}>No overheads configured.</td></tr>}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2" style={{ borderColor: RUST, background: '#FBF9F6' }}>
                      <td className="px-4 py-2 font-bold" style={{ color: RUST }} colSpan={3}>Overhead Cost</td>
                      <td className="px-4 py-2 text-right font-bold" style={{ color: RUST }}>{fmtRate(sheet.overhead_cost)}</td>
                    </tr>
                  </tfoot>
                </table>
              </Card>
            </div>
          )}


          <div className="mt-4">
            <SummaryTable />
          </div>


          {compareSnap && (
            <Card className="overflow-hidden mt-4" data-testid="costsheet-comparison">
              <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: '#F6E7DF' }}>
                <GitCompare className="w-4 h-4" style={{ color: RUST }} />
                <span className="text-sm font-semibold" style={{ color: RUST }}>
                  Comparison — now vs {compareSnap.label || 'Snapshot'} ({fmtDateTime(compareSnap.created_at)})
                </span>
                <div className="flex-1" />
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setCompareId('')} data-testid="costsheet-compare-clear"><X className="w-4 h-4" /></Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: '#FBF9F6', color: MUTED }} className="text-xs uppercase tracking-wide">
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-right">Cost/kg then → now</th>
                      <th className="px-3 py-2 text-right">Δ /kg</th>
                      <th className="px-3 py-2 text-right">Cost/bag then → now</th>
                      <th className="px-3 py-2 text-right">Δ /bag</th>
                      <th className="px-3 py-2 text-right">Margin% then → now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.map((c) => {
                      const dkg = c.then_kg == null ? null : c.now_kg - c.then_kg;
                      const dbag = c.then_bag == null ? null : c.now_bag - c.then_bag;
                      const DeltaCell = ({ v }) => v == null
                        ? <span style={{ color: MUTED }}>new</span>
                        : <span style={{ color: v > 0 ? RUST : (v < 0 ? GREEN : MUTED), fontWeight: 600 }}>{v > 0 ? '▲' : v < 0 ? '▼' : ''} {fmtRate(Math.abs(v))}</span>;
                      return (
                        <tr key={c.product_id} className="border-t" style={{ borderColor: '#F0EEE9' }} data-testid="costsheet-compare-row">
                          <td className="px-3 py-2 font-medium" style={{ color: '#1E2420' }}>{c.product_name}</td>
                          <td className="px-3 py-2 text-right tabular-nums" style={{ color: MUTED }}>{c.then_kg == null ? '—' : fmtRate(c.then_kg)} → <span style={{ color: GREEN, fontWeight: 600 }}>{fmtRate(c.now_kg)}</span></td>
                          <td className="px-3 py-2 text-right tabular-nums"><DeltaCell v={dkg} /></td>
                          <td className="px-3 py-2 text-right tabular-nums" style={{ color: MUTED }}>{c.then_bag == null ? '—' : fmtRate(c.then_bag)} → <span style={{ color: GREEN, fontWeight: 600 }}>{fmtRate(c.now_bag)}</span></td>
                          <td className="px-3 py-2 text-right tabular-nums"><DeltaCell v={dbag} /></td>
                          <td className="px-3 py-2 text-right tabular-nums" style={{ color: MUTED }}>{c.then_mpct == null ? '—' : `${fmt2(c.then_mpct)}%`} → <span style={{ color: (c.now_mpct ?? 0) >= 0 ? GREEN : RUST, fontWeight: 600 }}>{c.now_mpct == null ? 'N/A' : `${fmt2(c.now_mpct)}%`}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}


      {/* Overheads manager dialog */}
      <Dialog open={ohDialog} onOpenChange={(o) => { setOhDialog(o); if (!o && sheet) reCompute(); }}>
        <DialogContent data-testid="overheads-dialog">
          <DialogHeader>
            <DialogTitle>Manage Overheads</DialogTitle>
            <DialogDescription>Each head applies to all products or a chosen few. Basis = per batch, per kg, or per finished bag.</DialogDescription>
          </DialogHeader>
          <div className="mb-3 pb-3 border-b space-y-2" style={{ borderColor: '#F0EEE9' }}>
            <div className="grid grid-cols-12 gap-2">
              <Input className="col-span-5" placeholder="Name" value={ohForm.name} onChange={(e) => setOhForm((f) => ({ ...f, name: e.target.value }))} data-testid="overhead-name" />
              <div className="col-span-4">
                <Select value={ohForm.basis} onValueChange={(v) => setOhForm((f) => ({ ...f, basis: v }))}>
                  <SelectTrigger data-testid="overhead-basis"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_batch">Per Batch</SelectItem>
                    <SelectItem value="per_kg">Per kg</SelectItem>
                    <SelectItem value="per_bag">Per Bag</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input className="col-span-2" type="number" min="0" step="0.01" placeholder="₹" value={ohForm.amount} onChange={(e) => setOhForm((f) => ({ ...f, amount: e.target.value }))} data-testid="overhead-amount" />
              <Button className="col-span-1 text-white px-0" style={{ background: GREEN }} onClick={addOverhead} disabled={ohBusy} data-testid="overhead-add"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium" style={{ color: MUTED }}>Applies to:</span>
              <OverheadScope scope={ohForm.applies_to} productIds={ohForm.product_ids} products={formulations} onChange={(patch) => setOhForm((f) => ({ ...f, ...patch }))} />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y" style={{ borderColor: '#F0EEE9' }}>
            {overheads.map((oh) => (
              <div key={oh.id} className="py-2.5" data-testid="overhead-row">
                <div className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-5 h-8" defaultValue={oh.name} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== oh.name) updateOverhead(oh, { name: v }); }} />
                  <div className="col-span-4">
                    <Select value={oh.basis} onValueChange={(v) => updateOverhead(oh, { basis: v })}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_batch">Per Batch</SelectItem>
                        <SelectItem value="per_kg">Per kg</SelectItem>
                        <SelectItem value="per_bag">Per Bag</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Input className="col-span-2 h-8" type="number" min="0" step="0.01" defaultValue={oh.amount} onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v !== oh.amount) updateOverhead(oh, { amount: v }); }} />
                  <Button size="icon" variant="ghost" className="col-span-1 h-8 w-8" onClick={() => deleteOverhead(oh)} data-testid="overhead-delete"><Trash2 className="w-4 h-4" style={{ color: RUST }} /></Button>
                </div>
                <div className="flex items-center gap-2 mt-1.5 pl-1">
                  <span className="text-xs font-medium" style={{ color: MUTED }}>Applies to:</span>
                  <OverheadScope scope={oh.applies_to || 'all'} productIds={oh.product_ids} products={formulations} onChange={(patch) => updateOverhead(oh, patch)} />
                </div>
              </div>
            ))}
            {overheads.length === 0 && <p className="py-4 text-center text-sm" style={{ color: MUTED }}>No overheads yet.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOhDialog(false); if (sheet) reCompute(); }}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Snapshots dialog */}
      <Dialog open={snapDialog} onOpenChange={setSnapDialog}>
        <DialogContent data-testid="snapshots-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Camera className="w-5 h-5" style={{ color: GREEN }} /> Cost Snapshots</DialogTitle>
            <DialogDescription>Save a dated copy of the current all-products summary, then compare against it later as RM rates change.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mb-2">
            <Input placeholder="Optional label (e.g. Oct rates)" value={snapLabel} onChange={(e) => setSnapLabel(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveSnapshot(); }} data-testid="snapshot-label" />
            <Button className="text-white shrink-0" style={{ background: GREEN }} onClick={saveSnapshot} disabled={snapBusy || summaries.length === 0} data-testid="snapshot-save">
              {snapBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Camera className="w-4 h-4 mr-1.5" />} Save current
            </Button>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y" style={{ borderColor: '#F0EEE9' }}>
            {snapshots.map((s) => (
              <div key={s.id} className="flex items-center gap-2 py-2" data-testid="snapshot-row">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate" style={{ color: '#1E2420' }}>{s.label || 'Snapshot'}</p>
                  <p className="text-[11px]" style={{ color: MUTED }}>{fmtDateTime(s.created_at)} · {s.product_count} products · bag {fmt2(s.bag_weight_kg)}kg</p>
                </div>
                <Button size="sm" variant="outline" className="h-8" onClick={() => { setCompareId(s.id); setSnapDialog(false); }} data-testid="snapshot-compare"><GitCompare className="w-4 h-4 mr-1.5" /> Compare</Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeSnapshot(s)} data-testid="snapshot-delete"><Trash2 className="w-4 h-4" style={{ color: RUST }} /></Button>
              </div>
            ))}
            {snapshots.length === 0 && <p className="py-4 text-center text-sm" style={{ color: MUTED }}>No snapshots yet.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSnapDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
